/**
 * TurboTwin Cloud status page (docs/CONTRACT.md, section "Page").
 *
 * One anonymous DbConnection subscribes to the tables the page reads from the `turbotwin-status`
 * database and re-renders the page from the SDK's client cache whenever a row changes.
 * `#admin` mounts the operator console: the plain forms in index.html's `#admin-view` template,
 * whose one submit listener drives the reducers over a second, token-bearing connection; the
 * token lives in memory only. All data reaches the DOM through textContent and attributes on
 * elements built with createElement, never through innerHTML.
 */

import { DbConnection } from "./module_bindings";
import type {
  Component,
  ComponentState,
  Impact,
  Incident,
  IncidentStatus,
  IncidentUpdate,
  Maintenance,
  MaintenanceStatus,
  Overall,
  ProbeKind,
  Setting,
  Source,
  State,
  UptimeDay,
} from "./module_bindings/types";
import { InternalError, SenderError, Timestamp } from "spacetimedb";

// ------------------------------------------------------------------------------------ config

const DEFAULT_HOST = "wss://maincloud.spacetimedb.com";
const DEFAULT_DB = "turbotwin-status";
/** Replaced at build time by build.mjs. False in the production bundle, so `status.turbotwin.cloud/?db=…` cannot
 *  point the public page — under our domain — at someone else's database and show their "incidents" (review,
 *  defect 4). True only for local `--serve`/`--watch` builds, or a build with STATUS_ALLOW_OVERRIDE=1. */
declare const __ALLOW_TARGET_OVERRIDE__: boolean;
const params = new URLSearchParams(location.search);
const HOST = (__ALLOW_TARGET_OVERRIDE__ && params.get("host")) || DEFAULT_HOST;
const DB = (__ALLOW_TARGET_OVERRIDE__ && params.get("db")) || DEFAULT_DB;

const STALE_AFTER_MS = 120_000;
const CONNECT_GRACE_MS = 10_000;
/** SDK 2.10.1 reports nothing for a socket that never opens (verified against a refused port), so
 *  every attempt carries its own deadline after which it is abandoned and retried with backoff. */
const CONNECT_TIMEOUT_MS = 20_000;
const HISTORY_DAYS = 90;
const PAST_WINDOW_MS = 90 * 86_400_000;
const RECONNECT_BASE_MS = 2_000;
const RECONNECT_MAX_MS = 60_000;
/** How long a connection must stay subscribed before a later failure counts as a fresh one (backoff restarts). */
const HEALTHY_AFTER_MS = 30_000;

const ENV_ORDER = ["prod", "dev2", "dev", "external"];
const ENV_LABEL: Record<string, string> = {
  prod: "Production",
  dev2: "Development",
  // Retired 2026-09-27 (decision c); kept so a saved copy from before then still reads.
  dev: "Development (dev)",
  external: "Third-party services",
};

/** Components removed by decision (c), 2026-09-27, and the one that carries on for each. Since the 2026-09-23 cutover
 *  both probed the same cluster. Their resolved incidents stay in the history: they are labelled with the successor
 *  and, once the old key is gone, colour the successor's bar (rule U2) — incidents #1-#3 are the only record of the
 *  cutover outage on the customer hostname. */
const RETIRED: Record<string, string> = {
  "dev-api": "dev2-api",
  "dev-routing": "dev2-routing",
  "dev-control": "dev2-control",
};

/** Removed rows for internal tools (2026-09-30: Grafana and the admin UI are watched in Grafana only). Their resolved
 *  incidents stay in the database, but the page shows none of them: an incident that affects only these is left out,
 *  and they are dropped from any other incident's Affects line. */
const INTERNAL_REMOVED = new Set(["dev-admin", "dev2-admin"]);

/** An incident the page shows: it affects at least one component that is not an internal one. */
function isPublicIncident(inc: { components: string[] }): boolean {
  return inc.components.some((k) => !INTERNAL_REMOVED.has(k));
}

/** `key` itself while it is a component; its successor once it has been retired and removed. */
function currentKey(key: string, snap: Snapshot): string {
  return !snap.byKey.has(key) && RETIRED[key] ? RETIRED[key] : key;
}

/** The tables the page reads. Not probe_result, alert_map or operator: nothing on the page reads them (review, cuts).
 *  settings stays: staleness is judged against its probe_interval_secs. */
const SUBSCRIBED_TABLES = [
  "component",
  "component_state",
  "incident",
  "incident_update",
  "maintenance",
  "uptime_day",
  "overall",
  "settings",
];

// ------------------------------------------------------------------------------------ enums

type StateTag = State["tag"];
type ImpactTag = Impact["tag"];
type IncidentStatusTag = IncidentStatus["tag"];
type MaintenanceStatusTag = MaintenanceStatus["tag"];
type ProbeKindTag = ProbeKind["tag"];
type SourceTag = Source["tag"];
type Tone = "ok" | "warn" | "bad" | "info" | "none";

const STATE_TAGS: StateTag[] = ["Operational", "Degraded", "MajorOutage", "Maintenance", "Unknown"];
const IMPACT_TAGS: ImpactTag[] = ["None", "Minor", "Major", "Critical"];
const INCIDENT_STATUS_TAGS: IncidentStatusTag[] = ["Investigating", "Identified", "Monitoring", "Resolved"];
const PROBE_KIND_TAGS: ProbeKindTag[] = ["Ping", "Route", "Health", "External"];
const MAINTENANCE_STATUS_TAGS: MaintenanceStatusTag[] = ["Scheduled", "InProgress", "Completed", "Failed"];
const SOURCE_TAGS: SourceTag[] = ["Manual", "Deploy", "Alert", "Probe"];

/** Severity rank, mirrors logic.rs: Unknown < Operational < Maintenance < Degraded < MajorOutage. */
const SEVERITY: Record<StateTag, number> = {
  Unknown: 0,
  Operational: 1,
  Maintenance: 2,
  Degraded: 3,
  MajorOutage: 4,
};
const STATE_LABEL: Record<StateTag, string> = {
  Operational: "Operational",
  Degraded: "Degraded",
  MajorOutage: "Major outage",
  Maintenance: "Maintenance",
  Unknown: "Unknown",
};
const STATE_TONE: Record<StateTag, Tone> = {
  Operational: "ok",
  Degraded: "warn",
  MajorOutage: "bad",
  Maintenance: "info",
  Unknown: "none",
};
const IMPACT_LABEL: Record<ImpactTag, string> = {
  None: "No impact",
  Minor: "Minor",
  Major: "Major",
  Critical: "Critical",
};
const IMPACT_TONE: Record<ImpactTag, Tone> = { None: "none", Minor: "warn", Major: "bad", Critical: "bad" };
const INCIDENT_STATUS_LABEL: Record<IncidentStatusTag, string> = {
  Investigating: "Investigating",
  Identified: "Identified",
  Monitoring: "Monitoring",
  Resolved: "Resolved",
};
const MAINT_LABEL: Record<MaintenanceStatusTag, string> = {
  Scheduled: "Scheduled",
  InProgress: "In progress",
  Completed: "Completed",
  Failed: "Failed",
};
const MAINT_TONE: Record<MaintenanceStatusTag, Tone> = {
  Scheduled: "info",
  InProgress: "info",
  Completed: "ok",
  Failed: "bad",
};
const SOURCE_LABEL: Record<SourceTag, string> = {
  Manual: "Reported by our team",
  Deploy: "Posted by a deployment",
  Alert: "Raised by our monitoring",
  Probe: "Detected by our probes",
};
/** An incident's impact in the words a customer reads it in. */
const IMPACT_PHRASE: Record<ImpactTag, string> = {
  None: "Notice",
  Minor: "Minor impact",
  Major: "Major impact",
  Critical: "Critical impact",
};
const TONE_COLOR: Record<Tone, string> = {
  ok: "#1f9d61",
  warn: "#d68b0a",
  bad: "#d23f3f",
  info: "#2f7de1",
  none: "#aab3bd",
};

function worst(a: StateTag, b: StateTag): StateTag {
  return SEVERITY[b] > SEVERITY[a] ? b : a;
}

// ------------------------------------------------------------------------------------ DOM helpers

type Child = Node | string | null | undefined | false;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  append(node, children);
  return node;
}

function append(node: Node, children: Child[]): void {
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    node.appendChild(typeof c === "string" ? document.createTextNode(c) : c);
  }
}

function clear(node: Node): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

function mustGet(id: string): HTMLElement {
  const node = document.getElementById(id);
  if (!node) throw new Error(`index.html is missing #${id}`);
  return node;
}

/** Text that becomes a link only when it is an http(s) URL; anything else stays plain text. */
function linkOrText(text: string): HTMLElement {
  if (/^https?:\/\/\S+$/i.test(text)) {
    return el("a", { class: "message-link", href: text, rel: "noopener noreferrer", target: "_blank" }, text);
  }
  return el("span", { class: "message" }, text);
}

// ------------------------------------------------------------------------------------ time helpers

const DATE_TIME = new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" });
const DATE_ONLY = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });
const TIME_ONLY = new Intl.DateTimeFormat(undefined, { timeStyle: "medium" });

function tsMs(ts: Timestamp): number {
  return Number(ts.microsSinceUnixEpoch / 1000n);
}

function isEpoch(ts: Timestamp): boolean {
  return ts.microsSinceUnixEpoch === 0n;
}

function cmpBig(a: bigint, b: bigint): number {
  const d = a - b;
  return d < 0n ? -1 : d > 0n ? 1 : 0;
}

function cmpTs(a: Timestamp, b: Timestamp): number {
  return cmpBig(a.microsSinceUnixEpoch, b.microsSinceUnixEpoch);
}

/** A Timestamp as a Date, or null when it lies outside the range Date can represent
 *  (a Timestamp holds i64 microseconds; Date stops at ±8.64e15 ms, so a bogus value entered
 *  through the CLI is representable in the row but would make toISOString()/format() throw). */
function tsDate(ts: Timestamp): Date | null {
  const d = new Date(tsMs(ts));
  return Number.isNaN(d.getTime()) ? null : d;
}

function rawTs(ts: Timestamp): string {
  return `${ts.microsSinceUnixEpoch} µs since epoch`;
}

/** A Timestamp as text; an out-of-range value is shown as its raw micros instead of throwing. */
function fmtTs(ts: Timestamp, fmt: Intl.DateTimeFormat = DATE_TIME): string {
  const d = tsDate(ts);
  return d ? fmt.format(d) : rawTs(ts);
}

function timeEl(ts: Timestamp, fmt: Intl.DateTimeFormat = DATE_TIME): HTMLElement {
  const d = tsDate(ts);
  if (!d) return el("span", { class: "time-invalid", title: "timestamp outside the representable date range" }, rawTs(ts));
  const iso = d.toISOString();
  return el("time", { datetime: iso, title: `${iso} (UTC)` }, fmt.format(d));
}

type SpanUnit = "second" | "minute" | "hour" | "day";

/** A span of time as one whole unit: seconds below 2 minutes, minutes below 2 hours, hours below 2 days, then days.
 *  Never negative, so a viewer clock behind the server's never reads "in 5 seconds". */
function span(ms: number): [number, SpanUnit] {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 120) return [s, "second"];
  if (s < 7_200) return [Math.round(s / 60), "minute"];
  if (s < 172_800) return [Math.round(s / 3_600), "hour"];
  return [Math.round(s / 86_400), "day"];
}

// The wording comes from Intl (review, cuts). English, because these phrases sit inside the page's English
// sentences; dates and times above keep the viewer's own locale.
const RELATIVE = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
const unitFormat = (unit: SpanUnit): Intl.NumberFormat =>
  new Intl.NumberFormat("en", { style: "unit", unit, unitDisplay: "long" });
const DURATION: Record<SpanUnit, Intl.NumberFormat> = {
  second: unitFormat("second"),
  minute: unitFormat("minute"),
  hour: unitFormat("hour"),
  day: unitFormat("day"),
};

/** "3 minutes", "2 hours". */
function fmtDuration(ms: number): string {
  const [n, unit] = span(ms);
  return DURATION[unit].format(n);
}

/** "now", "3 minutes ago". */
function fmtAgo(ms: number): string {
  const [n, unit] = span(ms);
  return RELATIVE.format(-n, unit);
}

/** Uptime percentage, truncated (not rounded) so one failed probe never reads as 100 %. */
function fmtPct(p: number): string {
  if (p >= 100) return "100 %";
  return `${(Math.floor(p * 10) / 10).toFixed(1)} %`;
}

/** The last `n` UTC days as YYYY-MM-DD, oldest first, today last. */
function utcDays(n: number): string[] {
  const now = new Date();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const out: string[] = [];
  for (let i = n - 1; i >= 0; i--) out.push(new Date(today - i * 86_400_000).toISOString().slice(0, 10));
  return out;
}

// ------------------------------------------------------------------------------------ app state

type View = "status" | "admin";

interface App {
  /** The connection whose client cache is rendered: the newest one whose subscription has been
   *  applied. A reconnecting socket is promoted here only in onApplied, so the previous cache
   *  keeps showing (under the stale strip) instead of an empty page during the handover. */
  conn: DbConnection | null;
  /** The connection that currently owns the socket, applied or not. */
  socket: DbConnection | null;
  generation: number;
  connected: boolean;
  everConnected: boolean;
  /** True once `conn` holds an applied cache; stays true across reconnects. */
  subscribed: boolean;
  startedAt: number;
  lastMessageAt: number | null;
  connError: string | null;
  /** Set when render() threw; the previous DOM is kept and the strip explains. */
  renderError: string | null;
  reconnectAttempt: number;
  reconnectTimer: number | null;
  /** When the current connection's subscription was applied (local clock), or null. */
  appliedAt: number | null;
  /** From the last render: the newest probe, and how old it may get before the data counts as stale. */
  freshestProbeMs: number | null;
  /** LOCAL time at which freshestProbeMs last moved forward. Staleness is measured on this clock alone. */
  freshestSeenAt: number | null;
  staleAfterMs: number;
  /** Open disclosures (a service's details, a past incident), kept across re-renders: `svc:<key>`, `inc:<id>`. */
  expanded: Set<string>;
  /** The day being read out under a service's uptime bar, kept across re-renders. */
  reading: { key: string; index: number } | null;
  /** Incident history beyond the last 14 days. */
  showAllHistory: boolean;
  view: View;
}

const app: App = {
  conn: null,
  socket: null,
  generation: 0,
  connected: false,
  everConnected: false,
  subscribed: false,
  startedAt: Date.now(),
  lastMessageAt: null,
  connError: null,
  renderError: null,
  reconnectAttempt: 0,
  reconnectTimer: null,
  appliedAt: null,
  freshestProbeMs: null,
  freshestSeenAt: null,
  staleAfterMs: STALE_AFTER_MS,
  expanded: new Set(),
  reading: null,
  showAllHistory: false,
  view: "status",
};

const els = {
  app: mustGet("app"),
  stale: mustGet("stale"),
  footerSource: mustGet("footer-source"),
  footerTime: mustGet("footer-time"),
  favicon: mustGet("favicon"),
  adminView: mustGet("admin-view"),
  live: mustGet("live"),
  liveDot: mustGet("live-dot"),
  liveText: mustGet("live-text"),
};

// ------------------------------------------------------------------------------------ snapshot of the cache

interface Snapshot {
  components: Component[];
  byKey: Map<string, Component>;
  states: Map<string, ComponentState>;
  incidents: Incident[];
  updates: Map<string, IncidentUpdate[]>;
  maintenance: Maintenance[];
  activeMaintenance: Maintenance[];
  uptime: Map<string, Map<string, UptimeDay>>;
  overall: Map<string, Overall>;
  /** `probe_interval_secs` from settings, in ms; staleness is judged against it. */
  probeIntervalMs: number;
  /** False for the saved copy (snapshot/*.json), which is shown only while the live database is unreachable. */
  live: boolean;
}

function emptySnapshot(): Snapshot {
  return {
    components: [],
    byKey: new Map(),
    states: new Map(),
    incidents: [],
    updates: new Map(),
    maintenance: [],
    activeMaintenance: [],
    uptime: new Map(),
    overall: new Map(),
    probeIntervalMs: 60_000,
    live: true,
  };
}

/** The rows a view is built from, from either source: the live client cache or the saved copy. */
interface Rows {
  component: Iterable<Component>;
  componentState: Iterable<ComponentState>;
  incident: Iterable<Incident>;
  incidentUpdate: Iterable<IncidentUpdate>;
  maintenance: Iterable<Maintenance>;
  uptimeDay: Iterable<UptimeDay>;
  overall: Iterable<Overall>;
  settings: Iterable<Setting>;
}

function snapshot(conn: DbConnection | null): Snapshot {
  if (!conn) return emptySnapshot();
  const db = conn.db;
  return buildSnapshot(
    {
      component: db.component.iter(),
      componentState: db.componentState.iter(),
      incident: db.incident.iter(),
      incidentUpdate: db.incidentUpdate.iter(),
      maintenance: db.maintenance.iter(),
      uptimeDay: db.uptimeDay.iter(),
      overall: db.overall.iter(),
      settings: db.settings.iter(),
    },
    true
  );
}

function buildSnapshot(rows: Rows, live: boolean): Snapshot {
  const snap = emptySnapshot();
  snap.live = live;
  snap.components = [...rows.component].sort(
    (a, b) => a.sort - b.sort || a.name.localeCompare(b.name) || a.key.localeCompare(b.key)
  );
  for (const c of snap.components) snap.byKey.set(c.key, c);
  for (const s of rows.componentState) snap.states.set(s.componentKey, s);
  snap.incidents = [...rows.incident];
  for (const u of rows.incidentUpdate) {
    const k = String(u.incidentId);
    let list = snap.updates.get(k);
    if (!list) {
      list = [];
      snap.updates.set(k, list);
    }
    list.push(u);
  }
  for (const list of snap.updates.values()) list.sort((a, b) => cmpTs(b.at, a.at) || cmpBig(b.id, a.id));
  snap.maintenance = [...rows.maintenance];
  snap.activeMaintenance = snap.maintenance.filter((m) => m.status.tag === "InProgress");
  for (const d of rows.uptimeDay) {
    let days = snap.uptime.get(d.componentKey);
    if (!days) {
      days = new Map();
      snap.uptime.set(d.componentKey, days);
    }
    days.set(d.day, d);
  }
  for (const o of rows.overall) snap.overall.set(o.key, o);
  for (const st of rows.settings) {
    if (st.key !== "probe_interval_secs") continue;
    const secs = Number(st.value);
    if (Number.isFinite(secs) && secs > 0) snap.probeIntervalMs = secs * 1000;
  }
  return snap;
}

/** How old a probe may be before its result stops counting as current: three missed rounds, never under
 *  STALE_AFTER_MS. */
function staleAfterMs(snap: Snapshot): number {
  return Math.max(3 * snap.probeIntervalMs, STALE_AFTER_MS);
}

/** Newest `last_probe_at` over every probed component, or null when nothing has been probed yet. */
function freshestProbeMs(snap: Snapshot): number | null {
  let best: number | null = null;
  for (const c of snap.components) {
    const cs = snap.states.get(c.key);
    if (!c.probeUrl || !cs || isEpoch(cs.lastProbeAt)) continue;
    const t = tsMs(cs.lastProbeAt);
    if (best === null || t > best) best = t;
  }
  return best;
}

let lastSnapshot: Snapshot = emptySnapshot();

// ------------------------------------------------------------------------------------ the saved copy

// The nightly copy of the public tables ships with the page (snapshot/*.json, written by scripts/snapshot.py).
// When the live database cannot be reached — maincloud down, or blocked for this viewer — the page shows that
// copy instead of an endless "Connecting…", marked as saved everywhere it could be mistaken for live: the banner,
// every state pill (Unknown, with the last known state in its title) and the strip (review, "one job that fixes
// three problems"). The live data replaces it the moment a subscription is applied.

/** How long to wait before trying to load the saved copy again after a failed attempt. */
const SAVED_RETRY_MS = 60_000;
const saved = { snap: null as Snapshot | null, loading: false, triedAt: 0 };

class SavedCopyError extends Error {}

/** One row of the saved copy, read field by field. Any field that is not what the table promises throws, and
 *  then no saved copy is shown at all: a half-understood copy could mislabel a state. */
class SavedRow {
  constructor(
    private readonly row: Record<string, unknown>,
    private readonly where: string
  ) {}

  private bad(key: string, want: string): never {
    throw new SavedCopyError(`saved copy: ${this.where}.${key} is not ${want}`);
  }

  str(key: string): string {
    const v = this.row[key];
    return typeof v === "string" ? v : this.bad(key, "text");
  }

  int(key: string): number {
    const v = this.row[key];
    return typeof v === "number" && Number.isSafeInteger(v) && v >= 0 ? v : this.bad(key, "a whole number");
  }

  big(key: string): bigint {
    return BigInt(this.int(key));
  }

  bool(key: string): boolean {
    const v = this.row[key];
    return typeof v === "boolean" ? v : this.bad(key, "true or false");
  }

  strs(key: string): string[] {
    const v = this.row[key];
    return Array.isArray(v) && v.every((x) => typeof x === "string") ? (v as string[]) : this.bad(key, "a list of text");
  }

  /** snapshot.py writes times as ISO 8601 UTC to the millisecond. */
  time(key: string): Timestamp {
    const ms = Date.parse(this.str(key));
    return Number.isNaN(ms) ? this.bad(key, "a time") : Timestamp.fromDate(new Date(ms));
  }

  optTime(key: string): Timestamp | undefined {
    return this.row[key] === null ? undefined : this.time(key);
  }

  /** snapshot.py writes a variant as the server spells it ("inProgress"); the SDK's tag is "InProgress". */
  tag<T extends string>(key: string, allowed: readonly T[]): T {
    const v = this.str(key);
    const t = v.charAt(0).toUpperCase() + v.slice(1);
    return (allowed as readonly string[]).includes(t) ? (t as T) : this.bad(key, `one of ${allowed.join(", ")}`);
  }

  optTag<T extends string>(key: string, allowed: readonly T[]): T | undefined {
    return this.row[key] === null ? undefined : this.tag(key, allowed);
  }
}

async function savedTable<T>(table: string, read: (r: SavedRow) => T): Promise<T[]> {
  const response = await fetch(`./snapshot/${table}.json`, { cache: "no-cache" });
  if (!response.ok) throw new SavedCopyError(`snapshot/${table}.json: HTTP ${response.status}`);
  const rows: unknown = await response.json();
  if (!Array.isArray(rows)) throw new SavedCopyError(`snapshot/${table}.json is not a list`);
  return rows.map((row: unknown, i) => {
    if (typeof row !== "object" || row === null || Array.isArray(row)) {
      throw new SavedCopyError(`snapshot/${table}.json row ${i} is not an object`);
    }
    return read(new SavedRow(row as Record<string, unknown>, `${table}[${i}]`));
  });
}

async function loadSavedCopy(): Promise<void> {
  saved.loading = true;
  saved.triedAt = Date.now();
  try {
    const [component, componentState, incident, incidentUpdate, maintenance, uptimeDay, overall, settings] =
      await Promise.all([
        savedTable<Component>("component", (r) => ({
          key: r.str("key"),
          env: r.str("env"),
          group: r.str("group"),
          name: r.str("name"),
          description: r.str("description"),
          sort: r.int("sort"),
          probeUrl: r.str("probe_url"),
          probeKind: { tag: r.tag("probe_kind", PROBE_KIND_TAGS) },
        })),
        savedTable<ComponentState>("component_state", (r) => {
          const override = r.optTag("override_state", STATE_TAGS);
          return {
            componentKey: r.str("component_key"),
            state: { tag: r.tag("state", STATE_TAGS) },
            since: r.time("since"),
            reason: r.str("reason"),
            overrideState: override === undefined ? undefined : { tag: override },
            overrideReason: r.str("override_reason"),
            lastProbeAt: r.time("last_probe_at"),
            lastOkAt: r.time("last_ok_at"),
            consecutiveFail: r.int("consecutive_fail"),
            consecutiveOk: r.int("consecutive_ok"),
            build: r.str("build"),
          };
        }),
        savedTable<Incident>("incident", (r) => ({
          id: r.big("id"),
          title: r.str("title"),
          impact: { tag: r.tag("impact", IMPACT_TAGS) },
          status: { tag: r.tag("status", INCIDENT_STATUS_TAGS) },
          components: r.strs("components"),
          source: { tag: r.tag("source", SOURCE_TAGS) },
          openedAt: r.time("opened_at"),
          updatedAt: r.time("updated_at"),
          resolvedAt: r.optTime("resolved_at"),
          auto: r.bool("auto"),
          alertGroupKey: r.str("alert_group_key"),
        })),
        savedTable<IncidentUpdate>("incident_update", (r) => ({
          id: r.big("id"),
          incidentId: r.big("incident_id"),
          at: r.time("at"),
          status: { tag: r.tag("status", INCIDENT_STATUS_TAGS) },
          message: r.str("message"),
          author: r.str("author"),
        })),
        savedTable<Maintenance>("maintenance", (r) => ({
          id: r.big("id"),
          title: r.str("title"),
          components: r.strs("components"),
          startsAt: r.time("starts_at"),
          endsAt: r.time("ends_at"),
          status: { tag: r.tag("status", MAINTENANCE_STATUS_TAGS) },
          message: r.str("message"),
          source: { tag: r.tag("source", SOURCE_TAGS) },
          runId: r.str("run_id"),
        })),
        savedTable<UptimeDay>("uptime_day", (r) => ({
          id: r.big("id"),
          componentKey: r.str("component_key"),
          day: r.str("day"),
          okCount: r.int("ok_count"),
          failCount: r.int("fail_count"),
          worstState: { tag: r.tag("worst_state", STATE_TAGS) },
        })),
        savedTable<Overall>("overall", (r) => ({
          key: r.str("key"),
          state: { tag: r.tag("state", STATE_TAGS) },
          headline: r.str("headline"),
          updatedAt: r.time("updated_at"),
        })),
        savedTable<Setting>("settings", (r) => ({ key: r.str("key"), value: r.str("value") })),
      ]);
    saved.snap = buildSnapshot(
      { component, componentState, incident, incidentUpdate, maintenance, uptimeDay, overall, settings },
      false
    );
    scheduleRender();
  } catch (e) {
    // No copy (a fresh repository), a partial deploy, or a copy this page cannot read: stay on the plain
    // "cannot reach" strip, and try again later.
    console.warn("status page: no saved copy to show", e);
  } finally {
    saved.loading = false;
  }
}

// ------------------------------------------------------------------------------------ rendering: status view

const SVG_NS = "http://www.w3.org/2000/svg";
type Icon = "check" | "bang" | "cross" | "clock" | "question";
/** 16×16 stroke paths, drawn in currentColor on the glyph's coloured disc. */
const ICON_PATH: Record<Icon, string> = {
  check: "M4.2 8.4l2.5 2.5 5.1-5.6",
  bang: "M8 4.2v4.9M8 11.7v.1",
  cross: "M5.2 5.2l5.6 5.6M10.8 5.2l-5.6 5.6",
  clock: "M8 3.6V8l2.9 1.9",
  question: "M6.1 6.3a1.9 1.9 0 1 1 2.7 1.7c-.5.3-.8.6-.8 1.2v.3M8 11.7v.1",
};
const TONE_ICON: Record<Tone, Icon> = { ok: "check", warn: "bang", bad: "cross", info: "clock", none: "question" };

/** A state's disc with its icon, so a state never rests on colour alone. Decorative unless given a label. */
function glyph(tone: Tone, label?: string, icon: Icon = TONE_ICON[tone]): HTMLElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 16 16");
  svg.setAttribute("aria-hidden", "true");
  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("d", ICON_PATH[icon]);
  path.setAttribute("fill", "none");
  path.setAttribute("stroke", "currentColor");
  path.setAttribute("stroke-width", "2");
  path.setAttribute("stroke-linecap", "round");
  path.setAttribute("stroke-linejoin", "round");
  svg.appendChild(path);
  const attrs: Record<string, string> = { class: `glyph ${tone}` };
  if (label) {
    attrs.role = "img";
    attrs["aria-label"] = label;
  } else {
    attrs["aria-hidden"] = "true";
  }
  return el("span", attrs, svg);
}

/** A state as icon and word, in the state's colour. */
function stateEl(tone: Tone, text: string, title?: string): HTMLElement {
  const attrs: Record<string, string> = { class: `state ${tone}` };
  if (title) attrs.title = title;
  return el("span", attrs, glyph(tone), text);
}

/** Effective state for a service: override wins, then an in-progress maintenance lifts anything milder. */
function effectiveState(key: string, cs: ComponentState | undefined, active: Maintenance[]): StateTag {
  let s: StateTag = cs ? (cs.overrideState ? cs.overrideState.tag : cs.state.tag) : "Unknown";
  if (active.some((m) => m.components.includes(key))) s = worst(s, "Maintenance");
  return s;
}

function componentLabel(key: string, snap: Snapshot): string {
  const c = snap.byKey.get(currentKey(key, snap));
  return c ? `${c.name} (${ENV_LABEL[c.env] ?? c.env})` : key;
}

function openIncidents(snap: Snapshot): Incident[] {
  return snap.incidents
    .filter((i) => i.status.tag !== "Resolved")
    .sort((a, b) => cmpTs(b.openedAt, a.openedAt) || cmpBig(b.id, a.id));
}

function envCompare(a: string, b: string): number {
  const ia = ENV_ORDER.indexOf(a);
  const ib = ENV_ORDER.indexOf(b);
  const ra = ia < 0 ? ENV_ORDER.length : ia;
  const rb = ib < 0 ? ENV_ORDER.length : ib;
  return ra - rb || a.localeCompare(b);
}

function heroEl(tone: Tone, stateText: string, headline: string, sub: string | null, icon?: Icon): HTMLElement {
  return el(
    "section",
    { class: `hero ${tone}`, "aria-label": "Overall status" },
    glyph(tone, stateText, icon),
    el("h1", {}, headline),
    sub ? el("p", { class: "hero-sub" }, sub) : null
  );
}

/** One line per environment with its own verdict: what the page can say while production is not live. */
function envSummary(snap: Snapshot): HTMLElement {
  const wrap = el("div", { class: "hero-envs" });
  for (const env of [...snap.overall.keys()].filter((k) => k !== "all").sort(envCompare)) {
    const o = snap.overall.get(env);
    if (!o) continue;
    const tone = STATE_TONE[o.state.tag];
    wrap.appendChild(
      el("span", { class: "hero-env" }, glyph(tone), `${ENV_LABEL[env] ?? env}: ${STATE_LABEL[o.state.tag]}`)
    );
  }
  return wrap;
}

/** The answer to "is TurboTwin Cloud working?", first and largest. */
function renderHero(snap: Snapshot): HTMLElement {
  if (!snap.live) {
    // The saved copy: say so where every viewer looks first, with the time of its newest probe.
    const asOf = freshestProbeMs(snap);
    document.title = "Live status unavailable · TurboTwin Cloud status";
    setFavicon("none");
    const when = asOf === null ? "the last saved copy" : `the copy saved ${DATE_TIME.format(new Date(asOf))}`;
    return heroEl(
      "none",
      "Live status unavailable",
      "Live status is unavailable right now",
      `Showing ${when}. It may be out of date; the page keeps trying to reconnect.`
    );
  }
  const all = snap.overall.get("all");
  // The banner covers only the `banner_envs` setting (default prod). Once the data is in, no `all` row means none
  // of those envs has components yet. Before the production launch that is the normal state, not an unknown one,
  // so it is said plainly and in the neutral colour, with each running environment's own verdict under it; their
  // states never colour this banner (review, defect 8).
  if (!all && app.subscribed) {
    document.title = "TurboTwin Cloud status";
    setFavicon("none");
    const hero = heroEl(
      "none",
      "Pre-launch",
      "Production isn't live yet",
      "Its status will appear here at launch. These environments are running now:",
      "clock"
    );
    hero.appendChild(envSummary(snap));
    return hero;
  }
  const state: StateTag = all ? all.state.tag : "Unknown";
  const headline = all ? all.headline : app.subscribed ? "Status unknown" : "Checking status…";
  const tone = STATE_TONE[state];
  document.title = `${headline} · TurboTwin Cloud status`;
  setFavicon(tone);
  const open = openIncidents(snap).filter(isPublicIncident).length;
  const sub = open ? `${open} open incident${open === 1 ? "" : "s"}, details below.` : null;
  return heroEl(tone, STATE_LABEL[state], headline, sub);
}

const ENV_NOTE: Record<string, string> = {
  dev2: "Where new releases run before production. Expect more maintenance here.",
  external: "For information. We don't run these services, but a problem with them can affect you.",
};

function renderEnvs(snap: Snapshot): HTMLElement {
  const wrap = el("div", { class: "envs" });
  if (snap.components.length === 0) {
    wrap.appendChild(
      el(
        "p",
        { class: "empty" },
        app.subscribed ? "No services yet." : app.connected ? "Loading services…" : "Connecting to the status database…"
      )
    );
    return wrap;
  }
  const envs = [...new Set(snap.components.map((c) => c.env))].sort(envCompare);
  for (const env of envs) {
    const overall = snap.overall.get(env);
    const label = ENV_LABEL[env] ?? env;
    const head = el(
      "header",
      { class: "env-head" },
      el("h2", {}, label),
      !overall
        ? null
        : snap.live
          ? stateEl(STATE_TONE[overall.state.tag], STATE_LABEL[overall.state.tag], overall.headline)
          : stateEl("none", STATE_LABEL.Unknown, `last known: ${STATE_LABEL[overall.state.tag]} (saved copy)`),
      ENV_NOTE[env] ? el("p", { class: "env-note" }, ENV_NOTE[env]) : null
    );
    const list = el("ul", { class: "services" });
    for (const c of snap.components) if (c.env === env) list.appendChild(renderService(c, snap));
    wrap.appendChild(el("section", { class: "env", "aria-label": label }, head, list));
  }
  return wrap;
}

function detailRow(term: string, value: Child): HTMLElement[] {
  return [el("dt", {}, term), el("dd", {}, value)];
}

function renderService(c: Component, snap: Snapshot): HTMLElement {
  const cs = snap.states.get(c.key);
  // A service that has dropped out of the probe rotation must not keep showing its last state as if it were
  // current (review, smaller page items). An operator override is a person's explicit statement and still wins.
  // Measured against the newest probe of ANY service — two server times — so viewer clock skew cannot mark a
  // healthy service stale. When every probe stops, no service is singled out; the global strip covers that.
  const lastProbeMs = cs && c.probeUrl && !isEpoch(cs.lastProbeAt) ? tsMs(cs.lastProbeAt) : null;
  const newest = freshestProbeMs(snap);
  const lag = lastProbeMs !== null && newest !== null ? newest - lastProbeMs : 0;
  const stale = snap.live && lastProbeMs !== null && !cs?.overrideState && lag > staleAfterMs(snap);
  const known = effectiveState(c.key, cs, snap.activeMaintenance);
  // The saved copy may be hours old: no state is claimed from it; the details say what it was.
  const eff: StateTag = stale || !snap.live ? "Unknown" : known;

  const fk = `svc:${c.key}`;
  const open = app.expanded.has(fk);
  const detailsId = `svc-${c.key.replace(/[^A-Za-z0-9_-]/g, "_")}`;
  const details = el("dl", { class: "service-details", id: detailsId });
  details.hidden = !open;
  if (!snap.live) details.appendChild(el("p", { class: "note" }, `Last known: ${STATE_LABEL[known]}, in the saved copy.`));
  if (stale) {
    details.appendChild(
      el("p", { class: "note" }, `Last checked ${fmtDuration(lag)} before the other services, so its last state is not shown.`)
    );
  }
  const why = cs?.overrideState ? `Set by an operator: ${cs.overrideReason || "no reason given"}` : cs?.reason;
  if (why) details.append(...detailRow("Why", why.charAt(0).toUpperCase() + why.slice(1)));
  if (cs && !isEpoch(cs.since)) details.append(...detailRow("Since", timeEl(cs.since)));
  details.append(
    ...detailRow(
      "Last checked",
      !c.probeUrl ? "Not checked automatically" : cs && !isEpoch(cs.lastProbeAt) ? timeEl(cs.lastProbeAt) : "Not yet"
    )
  );
  if (cs?.build) details.append(...detailRow("Running build", cs.build));

  const head = el(
    "button",
    { class: "service-head", type: "button", "aria-expanded": String(open), "aria-controls": detailsId, "data-fk": fk },
    el(
      "span",
      { class: "service-name" },
      c.name,
      cs?.overrideState ? el("span", { class: "override-tag", title: "Status set by an operator" }, "set by operator") : null
    ),
    stateEl(STATE_TONE[eff], STATE_LABEL[eff])
  );
  head.addEventListener("click", () => {
    const nowOpen = details.hidden;
    details.hidden = !nowOpen;
    head.setAttribute("aria-expanded", String(nowOpen));
    if (nowOpen) app.expanded.add(fk);
    else app.expanded.delete(fk);
  });
  return el(
    "li",
    { class: "service" },
    head,
    c.description ? el("p", { class: "service-desc" }, c.description) : null,
    details,
    renderUptime(c.key, snap)
  );
}

/** Bar tones from mildest to worst; a day takes the worst of what the probes saw and what incidents declared. */
const TONE_RANK: Record<Tone, number> = { none: 0, ok: 1, info: 2, warn: 3, bad: 4 };
const IMPACT_BAR_TONE: Record<ImpactTag, Tone> = { None: "none", Minor: "warn", Major: "bad", Critical: "bad" };

function worseTone(a: Tone, b: Tone): Tone {
  return TONE_RANK[b] > TONE_RANK[a] ? b : a;
}

/** The worst impact among incidents naming `key` that were open at any moment of the UTC `day`, or null. */
function incidentOn(key: string, day: string, snap: Snapshot, nowMs: number): ImpactTag | null {
  const start = Date.parse(`${day}T00:00:00Z`);
  const end = start + 86_400_000;
  let worstImpact: ImpactTag | null = null;
  for (const inc of snap.incidents) {
    if (!inc.components.some((k) => currentKey(k, snap) === key)) continue;
    const opened = tsMs(inc.openedAt);
    const closed = inc.resolvedAt ? tsMs(inc.resolvedAt) : nowMs;
    if (opened >= end || closed < start) continue;
    const t = inc.impact.tag;
    if (worstImpact === null || TONE_RANK[IMPACT_BAR_TONE[t]] > TONE_RANK[IMPACT_BAR_TONE[worstImpact]]) worstImpact = t;
  }
  return worstImpact;
}

/** A UTC day key as the viewer reads a date: "Mon 28 Sept". */
const DAY_LABEL = new Intl.DateTimeFormat(undefined, { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });
const SHORT_DATE = new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", timeZone: "UTC" });

function dayLabel(day: string): string {
  return DAY_LABEL.format(new Date(`${day}T12:00:00Z`));
}

interface DayCell {
  tone: Tone | "pre";
  text: string;
}

/** The 90-day bar, per the uptime rules in docs/CONTRACT.md:
 *  U1 (module) probes inside a maintenance window are not counted; U2 a day's colour is the worst of the probes
 *  and of any incident open that day on this service; U3 a day without data is "no data", never "up", and the
 *  gaps are stated beside the percentage; U4 the percentage is measurement only — incidents colour, not count.
 *  Pointing at a day (mouse, touch, or the arrow keys once the bar has focus) reads it out under the bar. */
function renderUptime(key: string, snap: Snapshot): HTMLElement {
  const days = snap.uptime.get(key);
  const span = utcDays(HISTORY_DAYS);
  const firstDataDay = days && days.size ? [...days.keys()].sort()[0] : null;
  // Live: measured up to now. The saved copy knows nothing after its newest probe, so an incident open in the
  // copy is open only up to then, and the days after it are neither up, nor down, nor gaps.
  const asOfMs = snap.live ? Date.now() : (freshestProbeMs(snap) ?? Date.now());
  const today = snap.live ? span[span.length - 1] : new Date(asOfMs).toISOString().slice(0, 10);
  let ok = 0;
  let fail = 0;
  let gaps = 0;
  const cells: DayCell[] = [];
  for (const day of span) {
    const d = days?.get(day);
    let tone: Tone | "pre" = "none";
    const parts: string[] = [];
    if (!snap.live && day > today) {
      cells.push({ tone, text: "after the saved copy, no data" });
      continue;
    }
    if (d) {
      const total = d.okCount + d.failCount;
      const w = d.worstState.tag;
      if (w === "MajorOutage") tone = "bad";
      else if (w === "Degraded" || d.failCount > 0) tone = "warn";
      else if (w === "Maintenance") tone = "info";
      else if (total) tone = "ok";
      parts.push(total ? `${fmtPct((100 * d.okCount) / total)} up` : "maintenance only");
      if (w === "Maintenance") parts.push("planned maintenance");
      else if (total && w !== "Operational") parts.push(`worst: ${STATE_LABEL[w]}`);
      ok += d.okCount;
      fail += d.failCount;
    } else if (firstDataDay === null || day < firstDataDay) {
      // Before this service was first probed: not monitored yet, which is not the same as a gap.
      tone = "pre";
      parts.push("not monitored yet");
    } else {
      parts.push("no data");
      // A gap is a day after this service's first data and before today with nothing at all — the prober was not
      // running. Today may simply not have its first probe yet.
      if (day > firstDataDay && day !== today) gaps++;
    }
    const impact = incidentOn(key, day, snap, asOfMs);
    if (impact !== null && impact !== "None") {
      tone = worseTone(tone === "pre" ? "none" : tone, IMPACT_BAR_TONE[impact]);
      parts.push(`incident: ${IMPACT_LABEL[impact]}`);
    }
    cells.push({ tone, text: parts.join(", ") });
  }
  const total = ok + fail;
  const gapText = gaps ? `, no data for ${gaps} day${gaps === 1 ? "" : "s"}` : "";
  const pctText = total ? `${fmtPct((100 * ok) / total)} uptime${gapText}` : "No probe data yet";
  const since =
    firstDataDay !== null && firstDataDay > span[0] ? `Since ${SHORT_DATE.format(new Date(`${firstDataDay}T12:00:00Z`))}` : `${HISTORY_DAYS} days ago`;

  const fk = `bar:${key}`;
  const bar = el("div", {
    class: "bar",
    tabindex: "0",
    role: "img",
    "data-fk": fk,
    "aria-label": `Last ${HISTORY_DAYS} days, UTC: ${pctText}. Use the arrow keys to read each day.`,
  });
  const marks = cells.map((c) => el("i", { class: c.tone }));
  bar.append(...marks);
  const meta = el("div", { class: "uptime-meta", "aria-live": "polite" });
  const reset = (): void => {
    if (app.reading?.key === key) app.reading = null;
    for (const m of marks) m.classList.remove("at");
    meta.className = "uptime-meta";
    clear(meta);
    append(meta, [el("span", {}, since), el("span", { class: "uptime-pct" }, pctText), el("span", {}, "Today")]);
  };
  const show = (i: number): void => {
    const at = Math.max(0, Math.min(cells.length - 1, i));
    app.reading = { key, index: at };
    for (const [j, m] of marks.entries()) m.classList.toggle("at", j === at);
    meta.className = "uptime-meta reading";
    meta.textContent = `${dayLabel(span[at])}: ${cells[at].text}`;
  };
  const indexAt = (clientX: number): number => {
    const r = bar.getBoundingClientRect();
    return Math.floor(((clientX - r.left) / Math.max(1, r.width)) * cells.length);
  };
  bar.addEventListener("pointermove", (e) => show(indexAt(e.clientX)));
  bar.addEventListener("pointerdown", (e) => show(indexAt(e.clientX)));
  bar.addEventListener("pointerleave", () => {
    if (document.activeElement !== bar) reset();
  });
  bar.addEventListener("focus", () => show(app.reading?.key === key ? app.reading.index : cells.length - 1));
  bar.addEventListener("blur", reset);
  bar.addEventListener("keydown", (e) => {
    const cur = app.reading?.key === key ? app.reading.index : cells.length - 1;
    const next =
      e.key === "ArrowLeft" ? cur - 1 : e.key === "ArrowRight" ? cur + 1 : e.key === "Home" ? 0 : e.key === "End" ? cells.length - 1 : null;
    if (next === null) return;
    e.preventDefault();
    show(next);
  });
  // A re-render (every probe round) must not wipe a reading the viewer is in the middle of.
  if (app.reading?.key === key) show(app.reading.index);
  else reset();
  return el("div", { class: "uptime" }, bar, meta);
}

function renderAffects(all: string[], snap: Snapshot): HTMLElement | null {
  const components = all.filter((k) => !INTERNAL_REMOVED.has(k));
  if (!components.length) return null;
  return el(
    "p",
    { class: "affects" },
    el("span", { class: "label" }, "Affects "),
    components.map((k) => componentLabel(k, snap)).join(", ")
  );
}

function renderTimeline(updates: IncidentUpdate[]): HTMLElement {
  const ol = el("ol", { class: "timeline", "aria-label": "Updates, newest first" });
  if (!updates.length) ol.appendChild(el("li", { class: "empty" }, "No updates yet."));
  for (const u of updates) {
    ol.appendChild(
      el(
        "li",
        {},
        el(
          "div",
          { class: "entry-head" },
          el("span", { class: "status" }, INCIDENT_STATUS_LABEL[u.status.tag]),
          el("span", { class: "when" }, timeEl(u.at)),
          el("span", { class: "author" }, u.author === "system" ? "automatic" : u.author)
        ),
        el("p", { class: "message" }, u.message)
      )
    );
  }
  return ol;
}

const TIME_SHORT = new Intl.DateTimeFormat(undefined, { timeStyle: "short" });

function sameLocalDay(a: Date, b: Date): boolean {
  return a.toDateString() === b.toDateString();
}

/** "1 Oct 2026, 08:20 – 08:50", or both dates in full when the range crosses midnight. */
function rangeEl(from: Timestamp, to: Timestamp): Child[] {
  const a = tsDate(from);
  const b = tsDate(to);
  if (a && b && sameLocalDay(a, b)) return [timeEl(from), " – ", timeEl(to, TIME_SHORT)];
  return [timeEl(from), " – ", timeEl(to)];
}

// v1 scope (team decision 2026-09-21): the board is up/down uptime monitoring. The incident and maintenance
// sections render ONLY when they have something in them, so in normal operation the page is the answer at the top,
// the services and their uptime bars, and the history. What is happening now goes above the services, where a
// visitor who came because something is wrong looks first.
function renderActiveIncidents(snap: Snapshot): HTMLElement | null {
  const list = openIncidents(snap).filter(isPublicIncident);
  if (!list.length) return null;
  const title = snap.live ? "Happening now" : "Open in the saved copy";
  const sec = el("section", { class: "section" }, el("h2", {}, title));
  const now = Date.now();
  for (const inc of list) {
    const tone = IMPACT_TONE[inc.impact.tag];
    sec.appendChild(
      el(
        "article",
        { class: `notice-card ${tone}` },
        el("header", { class: "notice-head" }, stateEl(tone, IMPACT_PHRASE[inc.impact.tag]), el("h3", {}, inc.title)),
        el(
          "p",
          { class: "notice-when" },
          `${INCIDENT_STATUS_LABEL[inc.status.tag]}. Started `,
          timeEl(inc.openedAt),
          snap.live ? ` (${fmtAgo(now - tsMs(inc.openedAt))})` : "",
          `. ${SOURCE_LABEL[inc.source.tag]}.`
        ),
        renderAffects(inc.components, snap),
        renderTimeline(snap.updates.get(String(inc.id)) ?? [])
      )
    );
  }
  return sec;
}

function renderMaintenance(snap: Snapshot): HTMLElement | null {
  const list = snap.maintenance
    .filter((m) => m.status.tag === "Scheduled" || m.status.tag === "InProgress")
    .sort((a, b) => cmpTs(a.startsAt, b.startsAt) || cmpBig(a.id, b.id));
  if (!list.length) return null;
  const title = snap.live ? "Maintenance" : "Maintenance in the saved copy";
  const sec = el("section", { class: "section" }, el("h2", {}, title));
  for (const m of list) {
    const kind = m.source.tag === "Deploy" ? "A deployment" : "Planned maintenance";
    sec.appendChild(
      el(
        "article",
        { class: "notice-card info" },
        el("header", { class: "notice-head" }, stateEl("info", MAINT_LABEL[m.status.tag]), el("h3", {}, m.title)),
        el(
          "p",
          { class: "notice-when" },
          ...rangeEl(m.startsAt, m.endsAt),
          ` (${fmtDuration(tsMs(m.endsAt) - tsMs(m.startsAt))}). ${kind}.`
        ),
        renderAffects(m.components, snap),
        m.message ? el("p", { class: "affects" }, linkOrText(m.message)) : null
      )
    );
  }
  return sec;
}

/** Resolved incidents of the last 90 days, newest first, by the day they began. Two weeks show at first. */
const HISTORY_RECENT_MS = 14 * 86_400_000;
const HISTORY_DAY_FOLD = 3;

function renderHistory(snap: Snapshot): HTMLElement {
  const now = Date.now();
  const cutoff = now - PAST_WINDOW_MS;
  const endOf = (i: Incident): Timestamp => i.resolvedAt ?? i.updatedAt;
  const all = snap.incidents
    .filter((i) => i.status.tag === "Resolved" && tsMs(endOf(i)) >= cutoff && isPublicIncident(i))
    .sort((a, b) => cmpTs(b.openedAt, a.openedAt) || cmpBig(b.id, a.id));
  const recent = all.filter((i) => tsMs(i.openedAt) >= now - HISTORY_RECENT_MS);
  const shown = app.showAllHistory ? all : recent;
  const sec = el("section", { class: "section history" }, el("h2", {}, "Incident history"));
  if (!all.length) {
    sec.appendChild(el("p", { class: "history-quiet" }, "No incidents in the last 90 days."));
    return sec;
  }
  if (!shown.length) sec.appendChild(el("p", { class: "history-quiet" }, "No incidents in the last 14 days."));
  // By the day they began. A busy day shows its first three and folds the rest behind one button.
  const byDay = new Map<string, Incident[]>();
  for (const inc of shown) {
    const d = tsDate(inc.openedAt);
    const label = d ? DATE_ONLY.format(d) : rawTs(inc.openedAt);
    byDay.set(label, [...(byDay.get(label) ?? []), inc]);
  }
  for (const [label, incs] of byDay) {
    const fk = `day:${label}`;
    const open = app.expanded.has(fk) || incs.length <= HISTORY_DAY_FOLD + 1;
    const list = el("ul", { class: "history-list" });
    for (const inc of open ? incs : incs.slice(0, HISTORY_DAY_FOLD)) list.appendChild(el("li", {}, renderPast(inc, snap, endOf(inc))));
    if (!open) {
      const rest = incs.length - HISTORY_DAY_FOLD;
      const more = el("button", { class: "link-button", type: "button", "data-fk": fk }, `Show ${rest} more from this day`);
      more.addEventListener("click", () => {
        app.expanded.add(fk);
        scheduleRender();
      });
      list.appendChild(el("li", { class: "history-day-more" }, more));
    }
    const count = `${incs.length} incident${incs.length === 1 ? "" : "s"}`;
    sec.appendChild(el("div", { class: "history-day" }, el("h3", {}, `${label}, ${count}`), list));
  }
  const older = all.length - recent.length;
  if (older > 0) {
    const more = el(
      "button",
      { class: "link-button", type: "button", "data-fk": "history-more" },
      app.showAllHistory ? "Show the last 14 days only" : `Show ${older} older incident${older === 1 ? "" : "s"}`
    );
    more.addEventListener("click", () => {
      app.showAllHistory = !app.showAllHistory;
      scheduleRender();
    });
    sec.appendChild(el("p", { class: "history-more" }, more));
  }
  return sec;
}

function renderPast(inc: Incident, snap: Snapshot, end: Timestamp): HTMLElement {
  const fk = `inc:${inc.id}`;
  const tone = IMPACT_TONE[inc.impact.tag];
  const opened = tsDate(inc.openedAt);
  const lasted = `${opened ? TIME_SHORT.format(opened) : ""}, lasted ${fmtDuration(tsMs(end) - tsMs(inc.openedAt))}`;
  const details = el(
    "details",
    { class: "past" },
    el(
      "summary",
      { "data-fk": fk },
      glyph(tone, IMPACT_PHRASE[inc.impact.tag]),
      el("span", { class: "title" }, inc.title),
      el("span", { class: "lasted" }, lasted)
    ),
    el(
      "div",
      { class: "body" },
      el("p", { class: "notice-when" }, "Began ", timeEl(inc.openedAt), ", resolved ", timeEl(end), `. ${SOURCE_LABEL[inc.source.tag]}.`),
      renderAffects(inc.components, snap),
      renderTimeline(snap.updates.get(String(inc.id)) ?? [])
    )
  );
  details.open = app.expanded.has(fk);
  details.addEventListener("toggle", () => {
    if (details.open) app.expanded.add(fk);
    else app.expanded.delete(fk);
  });
  return details;
}

function setFavicon(tone: Tone): void {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16"><circle cx="8" cy="8" r="7" fill="${TONE_COLOR[tone]}"/></svg>`;
  els.favicon.setAttribute("href", `data:image/svg+xml,${encodeURIComponent(svg)}`);
}

/** The live indicator beats once when a new probe result lands. */
function beat(): void {
  const dot = els.liveDot;
  dot.classList.remove("beat");
  void dot.offsetWidth; // restart the animation
  dot.classList.add("beat");
}

// ------------------------------------------------------------------------------------ render loop

let renderQueued = false;

function scheduleRender(): void {
  if (renderQueued) return;
  renderQueued = true;
  requestAnimationFrame(() => {
    renderQueued = false;
    render();
  });
}

/** Any message from the database: a table event or a subscription being applied. */
function touch(): void {
  app.lastMessageAt = Date.now();
  scheduleRender();
}

function render(): void {
  try {
    renderNow();
    app.renderError = null;
  } catch (e) {
    // One bad row must not blank the page for everyone: keep the previous DOM (built before
    // the root is cleared) and say so in the strip; the next row change retries.
    console.error("status page: render failed", e);
    app.renderError = `Status page error: ${e instanceof Error ? e.message : String(e)} (showing the last successful view)`;
    if (!els.app.firstChild) els.app.appendChild(el("p", { class: "empty" }, "The status data could not be rendered; reload the page or check the browser console."));
  }
  tick();
}

function renderNow(): void {
  // Live data whenever a subscription has ever been applied; the saved copy only until then.
  const snap = app.conn ? snapshot(app.conn) : (saved.snap ?? emptySnapshot());
  lastSnapshot = snap;
  // Staleness never compares the viewer's clock with the server's: a laptop clock a few minutes fast would have
  // shown every component stale. Track instead, on the local clock, when the newest server probe time last
  // moved forward. A reconnect that brings no newer probe does not reset it. The saved copy's old probe times
  // must not seed this clock, or live data arriving later would be measured from them.
  if (snap.live) {
    const fresh = freshestProbeMs(snap);
    if (fresh !== null && (app.freshestProbeMs === null || fresh > app.freshestProbeMs)) {
      app.freshestProbeMs = fresh;
      app.freshestSeenAt = Date.now();
      beat();
    }
    app.staleAfterMs = staleAfterMs(snap);
  }
  if (app.view === "admin") {
    // The console writes to the live database, so it offers nothing from the saved copy: no stale incident or
    // window to pick, no old component row to pre-fill from.
    refreshAdminOptions(snap.live ? snap : emptySnapshot());
    return;
  }
  // Build everything first so an exception leaves the current DOM in place. What is happening now comes before the
  // services, since a visitor who came because something is wrong looks there first.
  const sections = [
    renderHero(snap),
    renderActiveIncidents(snap),
    renderMaintenance(snap),
    renderEnvs(snap),
    renderHistory(snap),
  ].filter((s): s is HTMLElement => s !== null);
  const root = els.app;
  // Every probe round re-renders the page; keep the keyboard where it was (elements that can hold focus carry a
  // stable data-fk).
  const focused = document.activeElement instanceof HTMLElement && root.contains(document.activeElement)
    ? document.activeElement.dataset.fk
    : undefined;
  clear(root);
  root.append(...sections);
  if (focused) root.querySelector<HTMLElement>(`[data-fk="${CSS.escape(focused)}"]`)?.focus({ preventScroll: true });
}

/** Once a second: relative times, the stale strip and the footer clock. */
function tick(): void {
  const now = Date.now();
  // The live indicator: how long ago this page last received a new probe result (local clock only).
  const live = app.conn !== null && app.connected && !app.connError;
  els.live.classList.toggle("on", live);
  els.liveText.textContent = !app.conn && saved.snap
    ? "Offline copy"
    : !app.connected
      ? app.everConnected ? "Reconnecting…" : "Connecting…"
      : app.freshestSeenAt === null
        ? "Live"
        : `Live, updated ${fmtAgo(now - app.freshestSeenAt)}`;

  // No live data yet, and either the first attempt has failed or it is taking too long: fetch the saved copy.
  if (
    !app.conn &&
    !saved.snap &&
    !saved.loading &&
    (app.connError !== null || now - app.startedAt > CONNECT_GRACE_MS) &&
    now - saved.triedAt > SAVED_RETRY_MS
  ) {
    void loadSavedCopy();
  }

  let stale: string | null = null;
  if (!app.conn && saved.snap) {
    const asOf = freshestProbeMs(saved.snap);
    const when = asOf === null ? "" : ` from ${DATE_TIME.format(new Date(asOf))} (${fmtAgo(now - asOf)})`;
    stale = `Live status unavailable: cannot reach the status database (${app.connError ?? "connecting"}). Showing the saved copy${when}; it may be out of date. Retrying…`;
  } else if (!app.connected) {
    if (app.everConnected) stale = `Status data stale: disconnected (${app.connError ?? "connection closed"}), reconnecting…`;
    else if (now - app.startedAt > CONNECT_GRACE_MS) stale = `Status data stale: cannot reach the status database (${app.connError ?? "connecting"})`;
  } else if (app.connError) {
    stale = `Status data stale: ${app.connError}`;
  } else if (app.freshestSeenAt !== null && now - app.freshestSeenAt > app.staleAfterMs) {
    // Keyed on the probes themselves, not on "any message" (which every reconnect reset), and measured on the
    // local clock only (see renderNow), so a skewed viewer clock cannot trigger or hide it.
    stale = `Status data stale: no new probe results for ${fmtDuration(now - app.freshestSeenAt)} — the probes may have stopped`;
  } else if (app.lastMessageAt !== null && now - app.lastMessageAt > STALE_AFTER_MS) {
    stale = `Status data stale: no update from the database for ${fmtDuration(now - app.lastMessageAt)}`;
  }
  if (app.renderError) stale = stale ? `${app.renderError} · ${stale}` : app.renderError;
  if (stale) {
    els.stale.textContent = stale;
    els.stale.hidden = false;
  } else {
    els.stale.hidden = true;
  }
  // A socket can open (and set lastMessageAt) without any data arriving; while the saved copy is on screen the
  // footer must not suggest a recent update.
  els.footerTime.textContent =
    !app.conn && saved.snap
      ? "— (showing the saved copy)"
      : app.lastMessageAt === null
        ? "—"
        : TIME_ONLY.format(new Date(app.lastMessageAt));
}

// ------------------------------------------------------------------------------------ connection

/** The three row callbacks every subscribed table exposes; a zero-arg listener fits all of them. */
interface Watchable {
  onInsert(cb: () => void): void;
  onDelete(cb: () => void): void;
  onUpdate(cb: () => void): void;
}

function watchTables(conn: DbConnection): void {
  const db = conn.db;
  const tables: Watchable[] = [
    db.component,
    db.componentState,
    db.incident,
    db.incidentUpdate,
    db.maintenance,
    db.uptimeDay,
    db.overall,
    db.settings,
  ];
  for (const t of tables) {
    t.onInsert(touch);
    t.onDelete(touch);
    t.onUpdate(touch);
  }
}

function connect(): void {
  if (app.reconnectTimer !== null) {
    clearTimeout(app.reconnectTimer);
    app.reconnectTimer = null;
  }
  const gen = ++app.generation;
  const live = (): boolean => gen === app.generation;
  let attempt: DbConnection;
  try {
    attempt = DbConnection.builder()
      .withUri(HOST)
      .withDatabaseName(DB)
      .onConnect((conn) => {
        if (!live()) {
          conn.disconnect();
          return;
        }
        // Not yet app.conn: its cache is empty until the subscription is applied, and the
        // previous cache (if any) is the better thing to show meanwhile.
        app.socket = conn;
        app.connected = true;
        app.everConnected = true;
        app.connError = null;
        // NOT app.reconnectAttempt = 0 here: a socket that opens but whose subscription then fails is not a
        // recovery. Resetting on connect made the subscription-error path below retry every 2 s for ever, from
        // every open viewer. See HEALTHY_AFTER_MS in scheduleReconnect().
        app.lastMessageAt = Date.now();
        watchTables(conn);
        conn
          .subscriptionBuilder()
          .onApplied(() => {
            if (!live()) return;
            app.conn = conn;
            app.subscribed = true;
            // Not a reset of the backoff yet: a subscription that applies and then fails (or a server that drops
            // sockets just after the initial apply) would otherwise cycle every 2 s, re-downloading every table.
            // scheduleReconnect() resets the counter only once a connection has stayed healthy for a while.
            app.appliedAt = Date.now();
            touch();
          })
          .onError((ctx) => {
            if (!live()) return;
            // A failed subscription does not heal by itself (after a module republish the old one is gone) and
            // the socket stays open, so the page used to sit stale until someone reloaded it (review, defect 5).
            // Abandon this connection and reconnect with the usual backoff (it grows, because the attempt counter
            // only resets after a connection stayed healthy for HEALTHY_AFTER_MS); the last applied data stays shown.
            // scheduleReconnect() bumps the generation first, so this socket's own onDisconnect is ignored.
            scheduleReconnect(`subscription failed (${ctx.event?.message ?? "unknown error"}), resubscribing`);
            try {
              conn.disconnect();
            } catch {
              // already closed
            }
          })
          .subscribe(SUBSCRIBED_TABLES.map((t) => `SELECT * FROM ${t}`));
        scheduleRender();
      })
      .onConnectError((_ctx, err) => {
        if (live()) scheduleReconnect(err.message || "connection error");
      })
      .onDisconnect((_ctx, err) => {
        if (live()) scheduleReconnect(err?.message ?? "connection closed");
      })
      .build();
  } catch (e) {
    scheduleReconnect(e instanceof Error ? e.message : String(e));
    return;
  }
  window.setTimeout(() => {
    if (!live() || app.connected) return;
    try {
      attempt.disconnect();
    } catch {
      // the socket never opened
    }
    scheduleReconnect(`no connection after ${CONNECT_TIMEOUT_MS / 1000} s`);
  }, CONNECT_TIMEOUT_MS);
}

/** Abandon the current attempt (its late callbacks are ignored) and retry with backoff.
 *  app.conn and app.subscribed are left alone: the last applied cache stays on screen. */
function scheduleReconnect(reason: string): void {
  // A connection that stayed healthy this long counts as a recovery, so the next failure starts the backoff over.
  // Anything shorter keeps growing it: failing on every connect, or applying and then failing straight away.
  if (app.appliedAt !== null && Date.now() - app.appliedAt > HEALTHY_AFTER_MS) app.reconnectAttempt = 0;
  app.appliedAt = null;
  app.generation++;
  app.connected = false;
  app.socket = null;
  app.connError = reason;
  if (app.reconnectTimer !== null) clearTimeout(app.reconnectTimer);
  const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** Math.min(app.reconnectAttempt, 5));
  app.reconnectAttempt++;
  app.reconnectTimer = window.setTimeout(connect, delay);
  scheduleRender();
}

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState !== "visible") return;
  if (app.reconnectTimer !== null) {
    connect();
  } else if (app.connected && app.socket?.isSocketClosed) {
    try {
      app.socket.disconnect();
    } catch {
      // already closed
    }
    scheduleReconnect("socket closed while the tab was inactive");
  }
});

// ------------------------------------------------------------------------------------ admin console

/** A reducer call that never got a reply (timeout, or the admin socket went away meanwhile). */
class NoReplyError extends Error {}

/** SDK 2.10.1 settles a reducer promise only when its result message arrives; a socket that
 *  drops mid-call leaves it pending for ever, so every call is raced against this deadline
 *  and against the admin connection going away. */
const CALL_TIMEOUT_MS = 15_000;

interface AdminState {
  conn: DbConnection | null;
  generation: number;
  connected: boolean;
  /** Rejecters of the reducer calls still waiting for a reply. */
  inflight: Set<(reason: string) => void>;
  tab: string;
}

const admin: AdminState = { conn: null, generation: 0, connected: false, inflight: new Set(), tab: "incidents" };

/** The mounted console, a clone of index.html's `#admin-view`; null on the status view. */
let adminRoot: HTMLElement | null = null;

/** An operator token typed into a page that arrived over plain HTTP could be read or the page altered on the way, so
 *  sign-in is offered only over HTTPS, or on this computer for a local build. */
const INSECURE_ORIGIN =
  location.protocol !== "https:" && !["localhost", "127.0.0.1", "[::1]"].includes(location.hostname);

/** Settle every pending reducer call with a NoReplyError (the admin socket is gone). */
function failInflight(reason: string): void {
  const pending = [...admin.inflight];
  admin.inflight.clear();
  for (const reject of pending) reject(reason);
}

/** The call, or a NoReplyError after CALL_TIMEOUT_MS or when the admin connection drops. */
function guarded(call: Promise<void>): Promise<void> {
  let rejectGuard: (reason: string) => void = () => undefined;
  let timer = 0;
  const guard = new Promise<void>((_resolve, reject) => {
    rejectGuard = (reason) => reject(new NoReplyError(reason));
    timer = window.setTimeout(() => rejectGuard(`no reply after ${CALL_TIMEOUT_MS / 1000} s`), CALL_TIMEOUT_MS);
  });
  admin.inflight.add(rejectGuard);
  // When the guard wins (timeout, dropped socket), `call` is left behind and may still reject later; without a
  // handler of its own that is an unhandled rejection. The race still sees `call`'s outcome when it wins.
  call.catch(() => undefined);
  return Promise.race([call, guard]).finally(() => {
    clearTimeout(timer);
    admin.inflight.delete(rejectGuard);
  });
}

function reducers(): DbConnection["reducers"] {
  if (!admin.conn || !admin.connected) throw new Error("Sign in with an operator token first.");
  return admin.conn.reducers;
}

// Form fields, read by their `name` in index.html. A bad value throws, and the form shows the message.

function text(f: FormData, name: string): string {
  const v = f.get(name);
  return typeof v === "string" ? v.trim() : "";
}

function label(name: string): string {
  return name.replace(/_/g, " ");
}

function bigId(f: FormData, name: string, what: string): bigint {
  const s = text(f, name);
  if (!/^\d+$/.test(s)) throw new Error(`Choose ${what}.`);
  return BigInt(s);
}

function u32(f: FormData, name: string): number {
  const s = text(f, name);
  const n = Number(s);
  if (!s || !Number.isInteger(n) || n < 0 || n > 0xffff_ffff) throw new Error(`The ${label(name)} must be a whole number, 0 or more.`);
  return n;
}

function tsOf(f: FormData, name: string): Timestamp {
  const d = new Date(text(f, name));
  if (Number.isNaN(d.getTime())) throw new Error(`Enter a date and time for ${label(name)}.`);
  return Timestamp.fromDate(d);
}

function tagOf<T extends string>(f: FormData, name: string, allowed: readonly T[]): T {
  const s = text(f, name);
  if (!(allowed as readonly string[]).includes(s)) throw new Error(`Choose one of: ${allowed.join(", ")}.`);
  return s as T;
}

/** One entry per action, keyed by the form's `data-reducer`, or the clicked button's. */
const ACTIONS: Record<string, (f: FormData) => Promise<void>> = {
  openIncident: async (f) =>
    reducers().openIncident({
      title: text(f, "title"),
      impact: { tag: tagOf(f, "impact", IMPACT_TAGS) },
      components: f.getAll("components").map(String),
      message: text(f, "message"),
    }),
  // "Resolved" is one of the statuses here: the module's resolve_incident is update_incident with Resolved.
  updateIncident: async (f) =>
    reducers().updateIncident({
      id: bigId(f, "id", "an incident"),
      status: { tag: tagOf(f, "status", INCIDENT_STATUS_TAGS) },
      message: text(f, "message"),
    }),
  scheduleMaintenance: async (f) => {
    const components = f.getAll("components").map(String);
    if (!components.length) throw new Error("Choose at least one affected service.");
    return reducers().scheduleMaintenance({
      title: text(f, "title"),
      components,
      startsAt: tsOf(f, "starts_at"),
      endsAt: tsOf(f, "ends_at"),
      message: text(f, "message"),
    });
  },
  startMaintenance: async (f) => reducers().startMaintenance({ id: bigId(f, "id", "a maintenance window") }),
  endMaintenance: async (f) =>
    reducers().endMaintenance({
      id: bigId(f, "id", "a maintenance window"),
      outcome: { tag: tagOf(f, "outcome", ["Completed", "Failed"] as const) },
    }),
  setOverride: async (f) =>
    reducers().setOverride({
      componentKey: text(f, "component_key"),
      // An empty state clears the override.
      state: text(f, "state") ? { tag: tagOf(f, "state", STATE_TAGS) } : undefined,
      reason: text(f, "reason"),
    }),
  upsertComponent: async (f) =>
    reducers().upsertComponent({
      key: text(f, "key"),
      env: text(f, "env"),
      group: text(f, "group") || text(f, "env"),
      name: text(f, "name"),
      description: text(f, "description"),
      sort: u32(f, "sort"),
      probeUrl: text(f, "probe_url"),
      probeKind: { tag: tagOf(f, "probe_kind", PROBE_KIND_TAGS) },
    }),
};

function describeError(e: unknown): string {
  if (e instanceof NoReplyError) {
    return `No reply (${e.message}). It may or may not have been applied: sign in again and check before you retry.`;
  }
  if (e instanceof SenderError) {
    if (/not an operator/.test(e.message)) {
      return "This account is not an operator. The database owner can make it one with the add_operator reducer.";
    }
    return `Refused: ${e.message}`;
  }
  if (e instanceof InternalError) return `Server error: ${e.message}`;
  if (e instanceof Error) return e.message;
  return String(e);
}

function setResult(out: HTMLElement, tone: "" | "ok" | "err", message: string): void {
  out.className = `result ${tone}`.trim();
  out.textContent = message;
}

function setAdminStatus(tone: "" | "ok" | "err", message: string): void {
  const status = adminRoot?.querySelector<HTMLElement>("#op-status");
  if (!status) return;
  status.className = `signin-status ${tone}`.trim();
  status.textContent = message;
}

/** The actions are usable only while signed in. */
function setActionsEnabled(on: boolean): void {
  const actions = adminRoot?.querySelector<HTMLFieldSetElement>("#op-actions");
  if (actions) actions.disabled = !on;
}

/** What each action reports back once the database has applied it. */
const DONE: Record<string, string> = {
  openIncident: "Published",
  updateIncident: "Update posted",
  scheduleMaintenance: "Window scheduled",
  startMaintenance: "Window started",
  endMaintenance: "Window ended",
  setOverride: "Override saved",
  upsertComponent: "Service saved",
};

/** The one submit listener: every form in the console lands here and calls its ACTIONS entry. */
function onAdminSubmit(ev: SubmitEvent): void {
  const form = ev.target;
  if (!(form instanceof HTMLFormElement)) return;
  ev.preventDefault();
  const submitter = ev.submitter instanceof HTMLButtonElement ? ev.submitter : null;
  const name = submitter?.dataset.reducer ?? form.dataset.reducer ?? "";
  const action = ACTIONS[name];
  const buttons = [...form.querySelectorAll<HTMLButtonElement>("button[type=submit]")];
  const out = form.querySelector("output");
  if (!action || !buttons.length || !out) throw new Error(`index.html: form "${name}" is not wired`);
  for (const b of buttons) b.disabled = true;
  setResult(out, "", "Sending…");
  guarded(action(new FormData(form)))
    .then(() => {
      if (form.hasAttribute("data-reset")) form.reset();
      setResult(out, "ok", `${DONE[name] ?? "Done"} at ${TIME_SHORT.format(new Date())}.`);
    })
    .catch((e: unknown) => setResult(out, "err", describeError(e)))
    .finally(() => {
      for (const b of buttons) b.disabled = false;
    });
}

/** "Start from an existing service" (the select marked data-prefill) fills the service form from the live row. */
function onAdminChange(ev: Event): void {
  const select = ev.target;
  if (!(select instanceof HTMLSelectElement) || !select.hasAttribute("data-prefill") || !select.form) return;
  if (!lastSnapshot.live) return;
  const c = lastSnapshot.byKey.get(select.value);
  if (!c) return;
  const values: Record<string, string> = {
    key: c.key,
    env: c.env,
    group: c.group,
    name: c.name,
    description: c.description,
    sort: String(c.sort),
    probe_url: c.probeUrl,
    probe_kind: c.probeKind.tag,
  };
  for (const [name, value] of Object.entries(values)) {
    const input = select.form.elements.namedItem(name);
    if (input instanceof HTMLInputElement || input instanceof HTMLSelectElement) input.value = value;
  }
}

function optionsFor(kind: string, snap: Snapshot): Array<[string, string]> {
  switch (kind) {
    case "components":
      return snap.components.map((c) => [c.key, `${c.name} (${ENV_LABEL[c.env] ?? c.env})`]);
    case "open-incidents":
      return openIncidents(snap).map((i) => [String(i.id), `${i.title} (#${i.id}, ${INCIDENT_STATUS_LABEL[i.status.tag]})`]);
    case "endable-maintenance":
      return snap.maintenance
        .filter((m) => m.status.tag === "Scheduled" || m.status.tag === "InProgress")
        .map((m) => [String(m.id), `${m.title} (#${m.id}, ${MAINT_LABEL[m.status.tag]}, from ${fmtTs(m.startsAt)})`]);
    default:
      throw new Error(`index.html: unknown data-options "${kind}"`);
  }
}

/** The open incidents, each with a shortcut to the update form. */
function renderOpenNow(snap: Snapshot): void {
  const box = adminRoot?.querySelector<HTMLElement>("#op-open-incidents");
  if (!box) return;
  const list = openIncidents(snap);
  const sig = JSON.stringify(list.map((i) => [String(i.id), i.title, i.status.tag, i.impact.tag, i.auto]));
  if (box.dataset.sig === sig) return;
  box.dataset.sig = sig;
  clear(box);
  box.appendChild(el("h3", {}, list.length ? `Open now (${list.length})` : "Open now"));
  if (!list.length) {
    box.appendChild(el("p", { class: "quiet" }, "No open incidents. If customers are affected, report one below."));
    return;
  }
  const ul = el("ul");
  for (const inc of list) {
    const go = el("button", { type: "button", class: "btn btn-quiet" }, "Post update");
    go.addEventListener("click", () => {
      const form = adminRoot?.querySelector<HTMLFormElement>('form[data-reducer="updateIncident"]');
      const id = form?.elements.namedItem("id");
      if (id instanceof HTMLSelectElement) id.value = String(inc.id);
      form?.querySelector("textarea")?.focus();
    });
    ul.appendChild(
      el(
        "li",
        {},
        glyph(IMPACT_TONE[inc.impact.tag], IMPACT_PHRASE[inc.impact.tag]),
        el(
          "div",
          {},
          el("strong", {}, inc.title),
          el(
            "p",
            { class: "meta" },
            `#${inc.id}, ${INCIDENT_STATUS_LABEL[inc.status.tag]}, opened ${fmtTs(inc.openedAt)}. `,
            inc.auto ? `${SOURCE_LABEL[inc.source.tag]}; it closes by itself.` : "Handled by an operator."
          )
        ),
        go
      )
    );
  }
  box.appendChild(ul);
}

/** Re-populate the data-driven selects and checkbox lists without losing the operator's current choice. */
function refreshAdminOptions(snap: Snapshot): void {
  if (!adminRoot) return;
  renderOpenNow(snap);
  for (const node of adminRoot.querySelectorAll<HTMLElement>("[data-options]")) {
    const opts = optionsFor(node.dataset.options ?? "", snap);
    // Probe rows change every minute but do not change these lists. Rebuilding anyway collapsed a list while the
    // operator was still choosing (review, smaller page items), so leave an unchanged list alone.
    const sig = JSON.stringify(opts);
    if (node.dataset.sig === sig) continue;
    node.dataset.sig = sig;
    if (node instanceof HTMLSelectElement) {
      const current = node.value;
      clear(node);
      node.appendChild(el("option", { value: "" }, opts.length ? "Choose…" : "Nothing to choose yet"));
      for (const [value, caption] of opts) {
        const o = el("option", { value }, caption);
        if (value === current) o.selected = true;
        node.appendChild(o);
      }
    } else {
      // A checkbox list: one box per option, named by data-name.
      const name = node.dataset.name ?? "components";
      const checked = new Set([...node.querySelectorAll<HTMLInputElement>("input:checked")].map((i) => i.value));
      clear(node);
      if (!opts.length) node.appendChild(el("span", { class: "quiet" }, "No services yet."));
      for (const [value, caption] of opts) {
        const box = el("input", { type: "checkbox", name, value });
        box.checked = checked.has(value);
        node.appendChild(el("label", {}, box, caption));
      }
    }
  }
}

function shortHex(hex: string): string {
  return hex.length > 16 ? `${hex.slice(0, 8)}…${hex.slice(-6)}` : hex;
}

function adminConnect(token: string): void {
  if (INSECURE_ORIGIN) {
    setAdminStatus("err", "Sign-in is off: this page was not loaded over HTTPS.");
    return;
  }
  if (!token) {
    setAdminStatus("err", "Paste an operator token first.");
    return;
  }
  // The SDK POSTs the token as a bearer to <HOST>/v1/identity/websocket-token before the socket opens. Only a local
  // build can carry a ?host= override (review, defect 4); even there, the token goes to another host only on a yes.
  if (HOST !== DEFAULT_HOST) {
    // Name the host as the SDK will parse it: URL parsing drops tabs and newlines, which the raw query value keeps
    // and a confirm dialog would print, so a padded ?host= could push the real host out of sight.
    let target: string;
    try {
      target = new URL(HOST).origin;
    } catch {
      setAdminStatus("err", "Not signed in: ?host= is not a URL.");
      return;
    }
    if (!window.confirm(`Send your operator token to ${target}? Only if you set this ?host= override yourself.`)) {
      // A cancel changes nothing: an admin connection that is already open stays open, and says so.
      if (admin.connected) setAdminStatus("ok", "Cancelled. You are still signed in.");
      else setAdminStatus("err", `Not signed in: the token was not sent to ${target}.`);
      return;
    }
  }
  adminDisconnect(null);
  const gen = ++admin.generation;
  const live = (): boolean => gen === admin.generation;
  setAdminStatus("", "Signing in…");
  try {
    admin.conn = DbConnection.builder()
      .withUri(HOST)
      .withDatabaseName(DB)
      .withToken(token)
      .onConnect((conn, identity) => {
        if (!live()) {
          conn.disconnect();
          return;
        }
        admin.connected = true;
        setActionsEnabled(true);
        // Not checked against the operator table, which the page does not subscribe to: the module refuses a
        // non-operator on the first action, and that form says so.
        setAdminStatus("ok", `Signed in as ${shortHex(identity.toHexString())}.`);
      })
      .onConnectError((_ctx, err) => {
        if (!live()) return;
        admin.connected = false;
        setActionsEnabled(false);
        failInflight(`connect error: ${err.message}`);
        setAdminStatus("err", `Could not sign in: ${err.message}`);
      })
      .onDisconnect((_ctx, err) => {
        if (!live()) return;
        admin.connected = false;
        setActionsEnabled(false);
        const why = err ? `Signed out: ${err.message}` : "Signed out.";
        failInflight(why);
        setAdminStatus("err", why);
      })
      .build();
  } catch (e) {
    setAdminStatus("err", describeError(e));
  }
}

function adminDisconnect(message: string | null): void {
  admin.generation++;
  try {
    admin.conn?.disconnect();
  } catch {
    // already closed
  }
  admin.conn = null;
  admin.connected = false;
  setActionsEnabled(false);
  failInflight("admin connection closed");
  if (message) setAdminStatus("", message);
}

/** Show one task panel and mark its tab. */
function selectTab(root: HTMLElement, name: string, focus = false): void {
  admin.tab = name;
  for (const tab of root.querySelectorAll<HTMLButtonElement>("[role=tab]")) {
    const on = tab.dataset.tab === name;
    tab.setAttribute("aria-selected", String(on));
    tab.tabIndex = on ? 0 : -1;
    if (on && focus) tab.focus();
  }
  for (const panel of root.querySelectorAll<HTMLElement>("[data-panel]")) panel.hidden = panel.dataset.panel !== name;
}

/** "Asia/Kolkata, UTC+05:30": the zone the maintenance times are read in. */
function zoneLabel(): string {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const off = -new Date().getTimezoneOffset();
  const sign = off >= 0 ? "+" : "−";
  const hh = String(Math.floor(Math.abs(off) / 60)).padStart(2, "0");
  const mm = String(Math.abs(off) % 60).padStart(2, "0");
  return `${zone}, UTC${sign}${hh}:${mm}`;
}

/** Clone the console out of index.html and wire it: sign-in, the tabs, and the forms. */
function mountAdmin(): HTMLElement {
  const root = els.adminView instanceof HTMLTemplateElement ? els.adminView.content.firstElementChild?.cloneNode(true) : null;
  if (!(root instanceof HTMLElement)) throw new Error("index.html: #admin-view must be a <template> holding one element");
  const part = <T extends Element>(selector: string): T => {
    const node = root.querySelector<T>(selector);
    if (!node) throw new Error(`index.html: #admin-view is missing ${selector}`);
    return node;
  };
  const token = part<HTMLInputElement>("#op-token");
  const connectBtn = part<HTMLButtonElement>("#op-connect");
  let where = HOST;
  try {
    where = new URL(HOST.replace(/^ws/, "http")).host;
  } catch {
    // shown as given
  }
  part("#op-target").textContent = `${DB} on ${where}`;
  if (INSECURE_ORIGIN) {
    part<HTMLElement>("#op-insecure").hidden = false;
    token.disabled = true;
    connectBtn.disabled = true;
  }
  connectBtn.addEventListener("click", () => adminConnect(token.value.trim()));
  part("#op-disconnect").addEventListener("click", () => adminDisconnect("Signed out."));
  token.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      adminConnect(token.value.trim());
    }
  });
  for (const tz of root.querySelectorAll<HTMLElement>("[data-tz]")) tz.textContent = zoneLabel();
  const tabs = [...root.querySelectorAll<HTMLButtonElement>("[role=tab]")];
  for (const [i, tab] of tabs.entries()) {
    tab.addEventListener("click", () => selectTab(root, tab.dataset.tab ?? "incidents"));
    tab.addEventListener("keydown", (e) => {
      const step = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
      if (!step) return;
      e.preventDefault();
      const next = tabs[(i + step + tabs.length) % tabs.length];
      selectTab(root, next.dataset.tab ?? "incidents", true);
    });
  }
  selectTab(root, admin.tab);
  root.addEventListener("submit", onAdminSubmit);
  root.addEventListener("change", onAdminChange);
  adminRoot = root;
  return root;
}

function teardownAdmin(): void {
  adminDisconnect(null);
  adminRoot = null;
}

// ------------------------------------------------------------------------------------ routing, boot

function route(): void {
  const wantAdmin = location.hash === "#admin";
  if (wantAdmin && app.view !== "admin") {
    app.view = "admin";
    clear(els.app);
    els.app.appendChild(mountAdmin());
    document.title = "Operator console · TurboTwin Cloud status";
  } else if (!wantAdmin && app.view !== "status") {
    app.view = "status";
    teardownAdmin();
  }
  render();
}

els.footerSource.textContent = HOST === DEFAULT_HOST ? "Data from SpacetimeDB maincloud" : `Data from ${HOST}`;
if (DB !== DEFAULT_DB) els.footerSource.textContent += ` · ${DB}`;

window.addEventListener("hashchange", route);
window.setInterval(tick, 1000);
route();
connect();
